import axios, { AxiosResponse } from "axios";
import { apiURL, apiKey } from "../shared/env";

/*Axios client utilities:
    - client: For API calls with API key authentication.
    - clientOkta: For Okta-secured endpoints with built-in 401 error handling and logging for common HTTP errors.
 Includes request timeout, credential handling, and flexible config overrides.*/

let isHandling401 = false;

export async function client(
  endpoint: string | string[],
  { data, method = "GET", params, headers: customHeaders, ...customConfig }: any = {}
) {

  const config = {
    url: `${apiURL}${endpoint}`,
    method: method || (data ? "POST" : "GET"),
    headers: {
      "Content-Type": data ? "application/json" : undefined,
      ...customHeaders,
      apikey: apiKey,
    },
    ...customConfig,
    withCredentials: true,
    timeout: 300000,
  };

  if (params) {
    config.params = params;
    config.method = "GET";
  }

  if (data) {
    config.data = data;
  }

  return axios(config)
    .then(async (response: AxiosResponse<any, any>) => {
      return response;
    })
    .catch((e: any) => {
      if (e.response?.status === 401) {
        const currentUrl = window.location.pathname + window.location.search;
        sessionStorage.setItem("redirectUrl", currentUrl);
        window.location.href = "/login";
      }
      return e;
    });
}

export async function clientOkta(
  endpoint: string | string[],
  { data, method = "GET", params, headers: customHeaders, ...customConfig }: any = {}
) {
  const config = {
    url: `${apiURL}${endpoint}`,
    method: method || (data ? "POST" : "GET"),
    headers: {
      "Content-Type": data ? "application/json" : undefined,
      ...customHeaders,
    },
    ...customConfig,
    withCredentials: true,
    timeout: 300000,
  };

  if (params) {
    config.params = params;
    config.method = "GET";
  }

  if (data) {
    config.data = data;
  }

  return axios(config)
    .then(async (response: AxiosResponse<any, any>) => {
      return response;
    })
    .catch(async (error: unknown) => {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const originalRequest: any = error.config;

        if (status === 401 && !originalRequest._handled401) {
          if (isHandling401) {
            return Promise.reject(error);
          }

          originalRequest._handled401 = true;
          isHandling401 = true;

          try {
            console.warn("Error 401: Unauthorized. Redirecting to login or refresh needed.");
          } finally {
            isHandling401 = false;
          }

          return Promise.reject(error);
        }

        switch (status) {
          case 400:
            console.error("Error 400: Bad request.");
            break;
          case 403:
            console.warn("Error 403: Forbidden. Access denied.");
            break;
          case 404:
            console.warn("Error 404: Resource not found.");
            break;
          case 500:
            console.error("Error 500: Internal server error.");
            break;
          default:
            console.error(`Unhandled error ${status}:`, error.response?.data);
        }
      } else if (error instanceof Error) {
        console.error("Unexpected error:", error.message);
      } else {
        console.error("Unknown error type:", error);
      }

      return Promise.reject(error);
    });
}